import { useState, useRef } from "react";
import { clsx } from "clsx";
import { IconPhoto, IconX, IconGripVertical } from "@tabler/icons-react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Text } from "../../../../components/common/typography";
import { IconPill } from "../../../../components/common/icon-pill";

function SortableMainImage({
  id,
  src,
  readOnly,
  uploading,
  uploadProgress,
  onRemove,
}: {
  id: string;
  src: string;
  readOnly: boolean;
  uploading: boolean;
  uploadProgress?: number;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative aspect-square w-full max-w-[20rem] overflow-hidden rounded-xl border border-borderDefault bg-bgLight"
    >
      <img src={src} alt="Product" className="h-full w-full object-cover" />
      {uploading && (
        <div className="absolute inset-0 flex items-center justify-center bg-textDark/60 text-bgWhite">
          <Text variant="bodyLgBold" tone="inherit">{uploadProgress}%</Text>
        </div>
      )}
      {!readOnly && (
        <>
          <button
            type="button"
            aria-label="Drag to reorder"
            {...attributes}
            {...listeners}
            className="absolute left-3 top-3 flex size-8 cursor-grab items-center justify-center rounded-full bg-bgWhite/90 shadow active:cursor-grabbing"
          >
            <IconGripVertical size={16} className="text-textDark" />
          </button>
          <IconPill
            as="button"
            size={32}
            tone="outline"
            shape="circle"
            aria-label="Remove image"
            onClick={onRemove}
            className="absolute right-3 top-3 shadow"
          >
            <IconX size={16} className="text-textDark" />
          </IconPill>
        </>
      )}
    </div>
  );
}

function SortableThumb({
  id,
  src,
  index,
  readOnly,
  onRemove,
}: {
  id: string;
  src: string;
  index: number;
  readOnly: boolean;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative aspect-square w-28 overflow-hidden rounded-xl border border-borderDefault bg-bgLight"
    >
      <img src={src} alt={`Product ${index + 2}`} className="h-full w-full object-cover" />
      {!readOnly && (
        <>
          <button
            type="button"
            aria-label="Drag to reorder"
            {...attributes}
            {...listeners}
            className="absolute left-2 top-2 flex size-6 cursor-grab items-center justify-center rounded-full bg-bgWhite/90 shadow active:cursor-grabbing"
          >
            <IconGripVertical size={14} className="text-textDark" />
          </button>
          <IconPill
            as="button"
            size={24}
            tone="outline"
            shape="circle"
            aria-label="Remove image"
            onClick={onRemove}
            className="absolute right-2 top-2 shadow"
          >
            <IconX size={12} className="text-textDark" />
          </IconPill>
        </>
      )}
    </div>
  );
}

export function ImageDropzone({
  images,
  onImagesChange,
  onFilesAdded,
  readOnly = false,
  uploadProgress,
}: {
  images: string[];
  onImagesChange: (imgs: string[]) => void;
  /** Surface raw File objects so the caller can upload them. */
  onFilesAdded?: (files: File[]) => void;
  readOnly?: boolean;
  /** 0..100 while a submit-time upload is in flight; renders an overlay. */
  uploadProgress?: number;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = images.indexOf(String(active.id));
    const newIndex = images.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    onImagesChange(arrayMove(images, oldIndex, newIndex));
  }
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  function handleFiles(files: FileList | null) {
    if (!files) return;
    const accepted = Array.from(files).filter((f) => f.type.startsWith("image/"));
    const urls = accepted.map((f) => URL.createObjectURL(f));
    onImagesChange([...images, ...urls]);
    onFilesAdded?.(accepted);
  }

  if (images.length === 0 && readOnly) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-borderDefault bg-bgLight py-16">
        <div className="flex size-12 items-center justify-center rounded-2xl bg-bgMuted">
          <IconPhoto size={24} className="text-textMuted" />
        </div>
        <Text variant="bodySm" tone="muted">No images attached</Text>
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(e) => e.key === "Enter" && fileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          handleFiles(e.dataTransfer.files);
        }}
        className={clsx(
          "flex cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed py-16 transition-colors outline-none",
          isDragging ? "border-brandPrimary bg-bgMuted" : "border-borderDefault bg-bgLight hover:border-borderDark",
        )}
      >
        <div className="flex size-12 items-center justify-center rounded-2xl bg-bgMuted">
          <IconPhoto size={24} className="text-textMuted" />
        </div>
        <div className="flex flex-col items-center gap-1">
          <Text variant="body" tone="body">Drag &amp; drop images here</Text>
          <Text variant="bodySm" tone="muted">or click to browse</Text>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>
    );
  }

  const uploading = uploadProgress != null;

  return (
    <div className="flex flex-col gap-3">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={images} strategy={horizontalListSortingStrategy}>
          <SortableMainImage
            id={images[0]}
            src={images[0]}
            readOnly={readOnly || uploading}
            uploading={uploading}
            uploadProgress={uploadProgress}
            onRemove={() => onImagesChange(images.filter((_, i) => i !== 0))}
          />
          {images.length > 1 && (
            <div className="flex flex-wrap gap-3">
              {images.slice(1).map((src, i) => (
                <SortableThumb
                  key={src}
                  id={src}
                  src={src}
                  index={i}
                  readOnly={readOnly}
                  onRemove={() => onImagesChange(images.filter((_, idx) => idx !== i + 1))}
                />
              ))}
            </div>
          )}
        </SortableContext>
      </DndContext>
      {images.length > 0 && !readOnly && (
        <Text variant="bodySm" tone="muted">
          The first image is the main product image. Drag any image to reorder; drag onto the big slot to make it the main.
        </Text>
      )}
      {!readOnly && (
        <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-borderDefault bg-bgLight py-4 transition-colors hover:bg-bgMuted">
          <IconPhoto size={18} className="text-textMuted" />
          <Text variant="bodySm" tone="muted">Add more images</Text>
          <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />
        </label>
      )}
    </div>
  );
}
